import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Play, Pause, Square, Trash2, RotateCcw, Bot, Clock, DollarSign, Zap,
} from 'lucide-react';
import {
  useInstance,
  usePauseInstance, useResumeInstance, useStopInstance, useDeleteInstance, useSpawnInstance,
} from '../hooks/useInstances';
import { InstanceStatus, SpawnMode } from '../types/instance';
import InstanceTerminal from '../components/InstanceTerminal';
import XTerminal from '../components/XTerminal';

const STATUS_CONFIG: Record<InstanceStatus, { color: string; bg: string; label: string }> = {
  [InstanceStatus.RUNNING]: { color: 'bg-green-500', bg: 'bg-green-50 text-green-700', label: 'Running' },
  [InstanceStatus.PAUSED]: { color: 'bg-yellow-500', bg: 'bg-yellow-50 text-yellow-700', label: 'Paused' },
  [InstanceStatus.STARTING]: { color: 'bg-blue-500', bg: 'bg-blue-50 text-blue-700', label: 'Starting' },
  [InstanceStatus.PENDING]: { color: 'bg-blue-400', bg: 'bg-blue-50 text-blue-700', label: 'Pending' },
  [InstanceStatus.COMPLETED]: { color: 'bg-slate-400', bg: 'bg-slate-100 text-slate-600', label: 'Completed' },
  [InstanceStatus.STOPPED]: { color: 'bg-slate-400', bg: 'bg-slate-100 text-slate-600', label: 'Stopped' },
  [InstanceStatus.FAILED]: { color: 'bg-red-500', bg: 'bg-red-50 text-red-700', label: 'Failed' },
};

export default function InstanceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: instance, isLoading } = useInstance(id);
  const pauseInstance = usePauseInstance();
  const resumeInstance = useResumeInstance();
  const stopInstance = useStopInstance();
  const deleteInstance = useDeleteInstance();
  const spawnInstance = useSpawnInstance();

  if (isLoading) {
    return <div className="p-8 text-sm text-slate-400">Loading instance...</div>;
  }

  if (!instance) {
    return <div className="p-8 text-sm text-slate-500">Instance not found.</div>;
  }

  const cfg = STATUS_CONFIG[instance.status];

  const handlePause = () => pauseInstance.mutate(instance.id);
  const handleResume = () => resumeInstance.mutate({ id: instance.id });
  const handleStop = () => stopInstance.mutate(instance.id);
  const handleDelete = () => {
    if (confirm('Delete this instance? This cannot be undone.')) {
      deleteInstance.mutate(instance.id);
      navigate(`/projects/${instance.projectId}/instances`);
    }
  };
  const handleSpawn = () => spawnInstance.mutate(instance.id);

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-slate-200 px-8 py-4">
        <button
          onClick={() => navigate(`/projects/${instance.projectId}/instances`)}
          className="mb-3 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to instances
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">
              {instance.agent?.name ?? 'Unknown agent'}
            </h1>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bg}`}>
              {cfg.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {instance.status === InstanceStatus.RUNNING && (
              <>
                <button
                  onClick={handlePause}
                  disabled={pauseInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <Pause className="h-3.5 w-3.5" />
                  Pause
                </button>
                <button
                  onClick={handleStop}
                  disabled={stopInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop
                </button>
              </>
            )}
            {instance.status === InstanceStatus.PAUSED && (
              <>
                <button
                  onClick={handleResume}
                  disabled={resumeInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Play className="h-3.5 w-3.5" />
                  Resume
                </button>
                <button
                  onClick={handleStop}
                  disabled={stopInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Square className="h-3.5 w-3.5" />
                  Stop
                </button>
              </>
            )}
            {instance.status === InstanceStatus.PENDING && (
              <>
                <button
                  onClick={handleSpawn}
                  disabled={spawnInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  <Play className="h-3.5 w-3.5" />
                  Spawn
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </>
            )}
            {(instance.status === InstanceStatus.COMPLETED ||
              instance.status === InstanceStatus.FAILED ||
              instance.status === InstanceStatus.STOPPED) && (
              <>
                <button
                  onClick={handleDelete}
                  disabled={deleteInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
                <button
                  onClick={handleSpawn}
                  disabled={spawnInstance.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Restart
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Bot className="h-3.5 w-3.5" />
            {instance.model}
          </span>
          <span className="flex items-center gap-1">
            <Zap className="h-3.5 w-3.5" />
            Turns: {instance.currentTurn}/{instance.maxTurns}
          </span>
          <span className="flex items-center gap-1">
            <DollarSign className="h-3.5 w-3.5" />
            ${Number(instance.totalCostUsd).toFixed(2)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {instance.totalTokens.toLocaleString()} tokens
          </span>
          {instance.task && (
            <span className="text-slate-400">
              Task: {instance.task.title}
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-8">
        {instance.spawnMode === SpawnMode.CLI ? (
          <XTerminal
            instanceId={instance.id}
            interactive={instance.status === InstanceStatus.RUNNING}
          />
        ) : (
          <InstanceTerminal instanceId={instance.id} status={instance.status} />
        )}
      </div>
    </div>
  );
}
